import React, {
  FC,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import {
  Toolbar,
  Box,
  Button,
  Popper,
  Paper,
  Grow,
  AppBar,
  useTheme,
} from "@mui/material";
import type { TopNavMenuProps } from "./types/topNavTypes";
import { makeLeftAnchor, safeTargetProps } from "./utils/topNavHelpers";
import {
  createTopButtonKeyDown,
  createCategoryKeyDown,
  createSubcategoryKeyDown,
} from "./utils/topNavKeyboardHandlers";
import {
  colors,
  toolbarBg,
  overlayStyle,
  dropdownPaperSx,
} from "./utils/topNavStyles";
import { SecondaryHeaderHtmlContent } from "./components/SecondaryHeaderHtmlContent";
import { TopNavCategoryList } from "./components/TopNavCategoryList";

export type { TopNavMenuProps };

export const TopNavMenu: FC<TopNavMenuProps> = ({ topNavMenuItems }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [parentButtonSelected, setParentButtonSelected] = useState<
    number | null
  >(null);

  const [categorySelected, setCategorySelected] = useState<number | null>(null);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [subcategoryPlacement, setSubcategoryPlacement] = useState<
    "right-start" | "right-end"
  >("right-start");

  const parentPaperRef = useRef<HTMLDivElement | null>(null);
  const subcategoryPaperRef = useRef<HTMLDivElement | null>(null);

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const topButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const categoryItemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const subcategoryItemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const openedViaKeyboard = useRef(false);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
    setParentButtonSelected(null);
    setCategorySelected(null);
    setCategoryAnchorEl(null);
    openedViaKeyboard.current = false;
  }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(closeMenu, 150);
  }, [closeMenu]);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  }, []);

  const centerAnchor = makeLeftAnchor(anchorEl);

  const handleButtonHover = (
    e: React.MouseEvent<HTMLElement>,
    key: number | undefined,
  ) => {
    cancelClose();

    if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);

    const currentTarget = e.currentTarget;
    hoverDebounceRef.current = setTimeout(() => {
      setAnchorEl(currentTarget);
      setParentButtonSelected(key ?? null);
      setCategorySelected(null);
      setCategoryAnchorEl(null);
    }, 175);
  };

  const handleCategoryHover = (
    e: React.MouseEvent<HTMLElement>,
    menuId: number | undefined,
  ) => {
    if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);

    const currentTarget = e.currentTarget;
    hoverDebounceRef.current = setTimeout(() => {
      setCategorySelected(menuId ?? null);
      setCategoryAnchorEl(currentTarget);
    }, 150);
  };

  const calculateSubcategoryPlacement = useCallback(() => {
    if (!parentPaperRef.current || !categoryAnchorEl) {
      return "right-start";
    }

    const parentRect = parentPaperRef.current.getBoundingClientRect();
    const categoryRect = categoryAnchorEl.getBoundingClientRect();
    const subcategoryRect =
      subcategoryPaperRef?.current?.getBoundingClientRect() ?? 0;
    if (subcategoryRect === 0) return "right-start";

    const wouldOverflow =
      categoryRect.y + subcategoryRect.height > parentRect.bottom;

    return wouldOverflow ? "right-end" : "right-start";
  }, [categoryAnchorEl]);

  useLayoutEffect(() => {
    if (categorySelected !== null && categoryAnchorEl) {
      setSubcategoryPlacement(calculateSubcategoryPlacement());
    }
  }, [categorySelected, categoryAnchorEl, calculateSubcategoryPlacement]);

  useEffect(() => {
    if (parentButtonSelected === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const triggerButton = topButtonRefs.current.get(parentButtonSelected);
        closeMenu();
        triggerButton?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [parentButtonSelected, closeMenu]);

  const selectedParentItem = topNavMenuItems?.find(
    (item) => item.menuId === parentButtonSelected,
  );

  useEffect(() => {
    if (parentButtonSelected === null || !openedViaKeyboard.current) return;

    const categories = selectedParentItem?.children ?? [];
    if (categories.length === 0) return;

    const firstId = categories[0]?.menuId;
    if (firstId == null) return;

    const timer = setTimeout(() => {
      categoryItemRefs.current.get(firstId)?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [parentButtonSelected]);

  const sortedTopNavItems = topNavMenuItems
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const keyboardDeps = {
    topButtonRefs,
    categoryItemRefs,
    subcategoryItemRefs,
    openedViaKeyboard,
    parentButtonSelected,
    categorySelected,
    closeMenu,
    setAnchorEl,
    setParentButtonSelected,
    setCategorySelected,
    setCategoryAnchorEl,
  };

  const handleTopButtonKeyDown = createTopButtonKeyDown(
    keyboardDeps,
    sortedTopNavItems,
  );
  const handleCategoryKeyDown = createCategoryKeyDown(keyboardDeps);
  const handleSubcategoryKeyDown = createSubcategoryKeyDown(keyboardDeps);

  return (
    <AppBar
      position="relative"
      elevation={1}
      sx={{
        marginTop: 0,
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar
        disableGutters
        variant="dense"
        sx={{ backgroundColor: toolbarBg(theme.palette.mode) }}
        role="menubar"
        onMouseLeave={scheduleClose}
        onMouseEnter={cancelClose}
      >
        <div style={{ display: "flex", marginLeft: 0 }}>
          {sortedTopNavItems.map((item, index) => {
            const label = item.displayValue;
            const isOpen = parentButtonSelected === item.menuId;
            return (
              <Button
                variant="text"
                key={item.menuId}
                ref={(el: HTMLButtonElement | null) => {
                  if (el && item.menuId != null) {
                    topButtonRefs.current.set(item.menuId, el);
                  }
                }}
                {...safeTargetProps(item.actionTarget)}
                role="menuitem"
                aria-haspopup="true"
                aria-expanded={isOpen}
                tabIndex={0}
                onClick={(e) => {
                  cancelClose();
                  if (hoverDebounceRef.current)
                    clearTimeout(hoverDebounceRef.current);
                  openedViaKeyboard.current = false;
                  setAnchorEl(e.currentTarget);
                  setParentButtonSelected(item.menuId ?? null);
                }}
                onKeyDown={(e) => handleTopButtonKeyDown(e, item, index)}
                onMouseEnter={(e) => handleButtonHover(e, item.menuId)}
                onMouseLeave={() => {
                  if (hoverDebounceRef.current)
                    clearTimeout(hoverDebounceRef.current);
                }}
                sx={{
                  color: colors.white,
                  height: { xs: "100%" },
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 16,
                  px: 2,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? colors.panelActiveDark
                        : colors.activeItem,
                  },
                }}
              >
                {label}
              </Button>
            );
          })}
        </div>

        <div style={{ flexGrow: 1 }} />

        {!!parentButtonSelected && (
          <div style={overlayStyle} aria-hidden="true" />
        )}
        <Popper
          open={!!parentButtonSelected}
          anchorEl={centerAnchor}
          placement="bottom-start"
          onMouseLeave={scheduleClose}
          onMouseEnter={cancelClose}
          transition
          sx={(theme) => ({ zIndex: theme.zIndex.modal })}
        >
          {({ TransitionProps }) => (
            <>
              <Grow {...TransitionProps}>
                <Paper
                  ref={parentPaperRef}
                  elevation={5}
                  sx={dropdownPaperSx}
                >
                  <TopNavCategoryList
                    selectedParentItem={selectedParentItem}
                    anchorEl={anchorEl}
                    categorySelected={categorySelected}
                    categoryAnchorEl={categoryAnchorEl}
                    subcategoryPlacement={subcategoryPlacement}
                    subcategoryPaperRef={subcategoryPaperRef}
                    categoryItemRefs={categoryItemRefs}
                    subcategoryItemRefs={subcategoryItemRefs}
                    hoverDebounceRef={hoverDebounceRef}
                    onCategoryHover={handleCategoryHover}
                    onCategoryKeyDown={handleCategoryKeyDown}
                    onSubcategoryKeyDown={handleSubcategoryKeyDown}
                    onCategoryDeselect={() => setCategorySelected(null)}
                    cancelClose={cancelClose}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      color: colors.white,
                    }}
                  >
                    <SecondaryHeaderHtmlContent
                      contentHtml={
                        selectedParentItem
                          ? selectedParentItem.contentValue
                          : ""
                      }
                    />
                  </Box>
                </Paper>
              </Grow>
            </>
          )}
        </Popper>
      </Toolbar>
    </AppBar>
  );
};
